(() => {
  const moneyFormat = window.Shopify && window.Shopify.money_format ? window.Shopify.money_format : "${{amount}}";
  const ZenTheme = window.ZenTheme || {};
  let cartDrawer = null;
  let cartDrawerOpener = null;
  let cartDrawerUpsells = [];
  const CART_REQUEST_TIMEOUT = 8000;

  window.ZenTheme = ZenTheme;

  ZenTheme.formatMoney = function(cents) {
    if (typeof cents === "string") cents = cents.replace(".", "");
    const value = (Number(cents || 0) / 100).toFixed(2);
    return moneyFormat.replace("${{amount}}", value).replace("${{ amount }}", value);
  };

  ZenTheme.updateCartCount = function(count) {
    const value = String(Number(count || 0));
    document.querySelectorAll(".zen-site-header__cart-count, .cart-count-badge, #cart-count").forEach((badge) => {
      badge.textContent = value;
      badge.classList.toggle("hidden", value === "0");
      if (value !== "0") badge.classList.add("flex");
    });
  };

  function escapeHtml(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    })[character]);
  }

  async function requestJson(url, options, timeoutMs) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), timeoutMs || CART_REQUEST_TIMEOUT);

    try {
      const response = await fetch(url, Object.assign({}, options || {}, { signal: controller.signal }));
      let json = null;

      try {
        json = await response.json();
      } catch (error) {
        json = null;
      }

      if (!response.ok) {
        const message = json && (json.description || json.message || json.status);
        throw new Error(message || "Cart request failed");
      }

      return json;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function createFallbackCartFromAdd(addResult, requestedQuantity) {
    const lines = Array.isArray(addResult && addResult.items)
      ? addResult.items
      : addResult
        ? [addResult]
        : [];
    const itemCount = lines.reduce((sum, line) => sum + Number(line.quantity || 0), 0) || Number(requestedQuantity || 1);
    const total = lines.reduce((sum, line) => {
      return sum + Number(line.final_line_price || line.line_price || line.price || 0);
    }, 0);

    return {
      item_count: itemCount,
      total_price: total,
      items: lines
    };
  }

  async function getFreshCartOrFallback(addResult, requestedQuantity) {
    try {
      return await ZenTheme.fetchCart();
    } catch (error) {
      return createFallbackCartFromAdd(addResult, requestedQuantity);
    }
  }

  function startCheckout(button, originalText) {
    const original = originalText || (button ? button.textContent : "");
    if (button) {
      button.disabled = true;
      button.textContent = "Checking out...";
    }

    window.location.assign("/checkout");
    window.setTimeout(() => {
      if (!/\/checkouts?|\/checkout/.test(window.location.pathname) && button) {
        button.disabled = false;
        button.textContent = original || "Checkout";
      }
    }, 6000);
  }

  ZenTheme.fetchCart = async function() {
    return requestJson("/cart.js", {
      headers: { "Accept": "application/json" }
    });
  };

  function getClientRouteRoot() {
    const route = document.documentElement.dataset.zenClientRoute || window.ZenClientRoute || "";
    if (!route) return document;
    return document.querySelector(`[data-zen-client-route="${route}"]`) || document;
  }

  function getCartDrawer() {
    if (!cartDrawer || !document.documentElement.contains(cartDrawer)) {
      const routeRoot = getClientRouteRoot();
      cartDrawer = routeRoot.querySelector("[data-zen-cart-drawer]") || document.querySelector("[data-zen-cart-drawer]");
    }
    return cartDrawer;
  }

  function getCartDrawerThreshold() {
    const drawer = getCartDrawer();
    return Number(drawer && drawer.dataset.freeShippingThreshold ? drawer.dataset.freeShippingThreshold : 6000);
  }

  function setCartDrawerLoading(isLoading) {
    const drawer = getCartDrawer();
    if (drawer) drawer.classList.toggle("is-loading", Boolean(isLoading));
  }

  function getSellingPlanName(item) {
    return item.selling_plan_allocation &&
      item.selling_plan_allocation.selling_plan &&
      item.selling_plan_allocation.selling_plan.name
      ? item.selling_plan_allocation.selling_plan.name
      : "";
  }

  function getSellingPlanId(item) {
    return item.selling_plan_allocation &&
      item.selling_plan_allocation.selling_plan &&
      item.selling_plan_allocation.selling_plan.id
      ? String(item.selling_plan_allocation.selling_plan.id)
      : "";
  }

  function getCartLineProperties(item) {
    if (!item.properties) return "";
    return Object.keys(item.properties).filter((key) => {
      return key.charAt(0) !== "_" && item.properties[key] !== "" && item.properties[key] != null;
    }).map((key) => {
      return `<span>${escapeHtml(key)}: ${escapeHtml(item.properties[key])}</span>`;
    }).join("");
  }

  function renderCartLine(item) {
    const sellingPlanName = getSellingPlanName(item);
    const variantTitle = item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : "";
    const properties = getCartLineProperties(item);
    const originalPrice = item.original_line_price > item.final_line_price
      ? `<s>${ZenTheme.formatMoney(item.original_line_price)}</s>`
      : "";
    const image = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
      : `<span aria-hidden="true"></span>`;

    return `
      <li class="zen-cart-drawer__item" data-zen-cart-line="${escapeHtml(item.key)}">
        <a class="zen-cart-drawer__image" href="${escapeHtml(item.url)}">${image}</a>
        <div class="zen-cart-drawer__line-main">
          <a class="zen-cart-drawer__line-title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          <div class="zen-cart-drawer__meta">
            ${variantTitle ? `<span>${escapeHtml(variantTitle)}</span>` : ""}
            ${sellingPlanName ? `<span>${escapeHtml(sellingPlanName)}</span>` : ""}
            ${properties}
          </div>
          <div class="zen-cart-drawer__line-controls">
            <div class="zen-cart-drawer__qty" aria-label="Quantity for ${escapeHtml(item.product_title)}">
              <button type="button" data-zen-cart-qty="decrease" data-line-key="${escapeHtml(item.key)}" data-quantity="${item.quantity - 1}" aria-label="Decrease quantity">-</button>
              <span>${item.quantity}</span>
              <button type="button" data-zen-cart-qty="increase" data-line-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="zen-cart-drawer__remove" data-zen-cart-remove data-line-key="${escapeHtml(item.key)}">Remove</button>
          </div>
        </div>
        <div class="zen-cart-drawer__line-price">
          ${originalPrice}
          <strong>${ZenTheme.formatMoney(item.final_line_price)}</strong>
        </div>
      </li>
    `;
  }

  function readCartDrawerUpsells() {
    const drawer = getCartDrawer();
    const script = drawer && drawer.querySelector("[data-zen-cart-upsells]");
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent || "[]");
      return Array.isArray(parsed) ? parsed.filter((upsell) => upsell && upsell.variantId) : [];
    } catch (error) {
      return [];
    }
  }

  function getCartUpsell(cart) {
    if (!cartDrawerUpsells.length) return null;
    return cartDrawerUpsells.find((upsell) => {
      return !(cart.items || []).some((item) => {
        const sameVariant = Number(item.variant_id) === Number(upsell.variantId);
        if (!sameVariant) return false;
        return upsell.sellingPlanId ? getSellingPlanId(item) === String(upsell.sellingPlanId) : true;
      });
    });
  }

  function renderCartUpsell(cart) {
    const drawer = getCartDrawer();
    const container = drawer && drawer.querySelector("[data-zen-cart-upsell]");
    if (!container) return;
    const upsell = cart && cart.item_count ? getCartUpsell(cart) : null;
    if (!upsell) {
      container.hidden = true;
      container.innerHTML = "";
      return;
    }
    container.hidden = false;
    container.innerHTML = `
      <div class="zen-cart-drawer__upsell-card">
        <div class="zen-cart-drawer__upsell-image">
          ${upsell.image ? `<img src="${escapeHtml(upsell.image)}" alt="${escapeHtml(upsell.title)}" loading="lazy">` : ""}
        </div>
        <div class="zen-cart-drawer__upsell-copy">
          <span>${escapeHtml(upsell.eyebrow || "Subscribe and save")}</span>
          <strong>${escapeHtml(upsell.title)}</strong>
          <small>${escapeHtml(upsell.subtitle || "")}</small>
          ${upsell.price ? `<em>${escapeHtml(upsell.price)}</em>` : ""}
        </div>
        <button
          type="button"
          class="zen-cart-drawer__upsell-button"
          data-zen-cart-upsell-add
          data-variant-id="${escapeHtml(upsell.variantId)}"
          data-selling-plan-id="${escapeHtml(upsell.sellingPlanId || "")}"
          data-quantity="${escapeHtml(upsell.quantity || 1)}"
        >
          + Add
        </button>
      </div>
    `;
  }

  ZenTheme.renderCartDrawer = function(cart) {
    const drawer = getCartDrawer();
    if (!drawer || !cart) return;
    const items = drawer.querySelector("[data-zen-cart-items]");
    const empty = drawer.querySelector("[data-zen-cart-empty]");
    const footer = drawer.querySelector("[data-zen-cart-footer]");
    const subtotal = drawer.querySelector("[data-zen-cart-subtotal]");
    const progress = drawer.querySelector("[data-zen-cart-progress]");
    const progressText = drawer.querySelector("[data-zen-cart-progress-text]");
    const hasItems = Number(cart.item_count || 0) > 0;
    const threshold = getCartDrawerThreshold();
    const progressValue = threshold > 0 ? Math.min(100, Math.round((cart.total_price / threshold) * 100)) : 100;
    const remaining = Math.max(0, threshold - cart.total_price);

    if (items) items.innerHTML = hasItems ? cart.items.map(renderCartLine).join("") : "";
    if (empty) empty.hidden = hasItems;
    if (footer) footer.hidden = !hasItems;
    if (subtotal) subtotal.textContent = ZenTheme.formatMoney(cart.total_price);
    if (progress) progress.style.width = `${progressValue}%`;
    if (progressText) {
      progressText.textContent = remaining > 0
        ? `${ZenTheme.formatMoney(remaining)} away from free shipping`
        : "Free shipping unlocked";
    }
    renderCartUpsell(cart);
  };

  ZenTheme.openCartDrawer = async function(cart) {
    const drawer = getCartDrawer();
    if (!drawer) return;
    cartDrawerOpener = document.activeElement;
    let activeCart = cart;
    if (!activeCart) {
      try {
        setCartDrawerLoading(true);
        activeCart = await ZenTheme.fetchCart();
      } catch (error) {
        window.location.assign("/cart");
        return;
      } finally {
        setCartDrawerLoading(false);
      }
    }
    ZenTheme.renderCartDrawer(activeCart);
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("zen-cart-drawer-open");
    const closeButton = drawer.querySelector("[data-zen-cart-close]");
    if (closeButton) closeButton.focus({ preventScroll: true });
  };

  ZenTheme.closeCartDrawer = function() {
    const drawer = getCartDrawer();
    if (!drawer) return;
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("zen-cart-drawer-open");
    if (cartDrawerOpener && typeof cartDrawerOpener.focus === "function") {
      cartDrawerOpener.focus({ preventScroll: true });
    }
  };

  async function changeCartLine(lineKey, quantity) {
    setCartDrawerLoading(true);
    try {
      const cart = await requestJson("/cart/change.js", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ id: lineKey, quantity: Math.max(0, Number(quantity || 0)) })
      });
      ZenTheme.updateCartCount(cart.item_count);
      ZenTheme.renderCartDrawer(cart);
      document.dispatchEvent(new CustomEvent("zen:cart:updated", { detail: { cart } }));
    } finally {
      setCartDrawerLoading(false);
    }
  }

  async function addCartUpsell(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;
    const original = button.textContent || "+ Add";
    const item = {
      id: Number(variantId),
      quantity: Math.max(1, Number(button.dataset.quantity || 1))
    };
    if (button.dataset.sellingPlanId) item.selling_plan = Number(button.dataset.sellingPlanId);
    button.disabled = true;
    button.textContent = "Adding...";
    setCartDrawerLoading(true);
    try {
      const addResult = await requestJson("/cart/add.js", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ items: [item] })
      });
      const cart = await getFreshCartOrFallback(addResult, item.quantity);
      ZenTheme.updateCartCount(cart.item_count);
      ZenTheme.renderCartDrawer(cart);
      document.dispatchEvent(new CustomEvent("zen:cart:updated", { detail: { cart } }));
      ZenTheme.openCartDrawer(cart);
    } catch (error) {
      button.textContent = "Try again";
      setTimeout(() => {
        button.disabled = false;
        button.textContent = "+ Add";
      }, 1200);
      return;
    } finally {
      setCartDrawerLoading(false);
    }
  }

  function initCartDrawer() {
    const drawer = getCartDrawer();
    if (!drawer) return;
    cartDrawerUpsells = readCartDrawerUpsells();

    drawer.addEventListener("click", (event) => {
      const target = event.target;
      if (target.closest("[data-zen-cart-close]")) {
        event.preventDefault();
        ZenTheme.closeCartDrawer();
        return;
      }
      const checkout = target.closest("[data-zen-cart-checkout]");
      if (checkout) {
        event.preventDefault();
        startCheckout(checkout);
        return;
      }
      const quantityButton = target.closest("[data-zen-cart-qty]");
      if (quantityButton) {
        event.preventDefault();
        changeCartLine(quantityButton.dataset.lineKey, quantityButton.dataset.quantity);
        return;
      }
      const removeButton = target.closest("[data-zen-cart-remove]");
      if (removeButton) {
        event.preventDefault();
        changeCartLine(removeButton.dataset.lineKey, 0);
        return;
      }
      const upsellButton = target.closest("[data-zen-cart-upsell-add]");
      if (upsellButton) {
        event.preventDefault();
        addCartUpsell(upsellButton);
      }
    });

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-zen-cart-open], .zen-site-header__cart[href]");
      if (!trigger || !getCartDrawer()) return;
      event.preventDefault();
      ZenTheme.openCartDrawer();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && drawer.classList.contains("is-open")) {
        ZenTheme.closeCartDrawer();
      }
    });

    document.addEventListener("zen:cart:updated", (event) => {
      if (event.detail && event.detail.cart) ZenTheme.renderCartDrawer(event.detail.cart);
    });

    ZenTheme.fetchCart().then((cart) => {
      ZenTheme.updateCartCount(cart.item_count);
      ZenTheme.renderCartDrawer(cart);
    }).catch(() => {});
  }

  document.addEventListener("submit", async (event) => {
    const form = event.target;
    if (!form.matches("[data-zen-product-form]")) return;
    if (event.defaultPrevented || form.hasAttribute("data-zen-skip-ajax")) return;
    if (!window.fetch) return;
    event.preventDefault();
    const button = form.querySelector("[type='submit']");
    const original = button ? button.innerHTML : "";
    let shouldRestore = true;
    if (button) {
      button.disabled = true;
      button.innerHTML = "Adding...";
    }
    try {
      const formData = new FormData(form);
      const requestedQuantity = formData.get("quantity") || 1;
      const addResult = await requestJson("/cart/add.js", {
        method: "POST",
        headers: { "Accept": "application/json" },
        body: formData
      });
      const cart = await getFreshCartOrFallback(addResult, requestedQuantity);
      ZenTheme.updateCartCount(cart.item_count);
      if (button) button.innerHTML = "Added";
      document.dispatchEvent(new CustomEvent("zen:cart:updated", { detail: { cart } }));
      ZenTheme.openCartDrawer(cart);
    } catch (error) {
      if (button) button.innerHTML = "Try again";
      form.setAttribute("data-error", "true");
    } finally {
      setTimeout(() => {
        if (shouldRestore && button) {
          button.disabled = false;
          button.innerHTML = original;
        }
      }, 1200);
    }
  });

  initCartDrawer();
})();
